import React, { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import useKeyDownListener from 'hooks/useKeyDownListener';
import SuppliersSvg from 'images/v5/icons/menu/suppliers.svg';
import Input from 'layout/atoms/Input/Input';
import Label from 'layout/atoms/Label/Label';
import Modal from 'layout/atoms/Modal/Modal';
import FormField from 'layout/molecules/FormField/FormField';
import FormSaveActions from 'layout/molecules/FormSaveActions/FormSaveActions';
import Grid from 'layout/molecules/Grid/Grid';
import { closeModal, selectModalByName } from 'store/layoutSlice';
import { updateSupplier } from 'store/suppliersSlice';
import useSupplierValidation from '../useSupplierValidation';
function SupplierEditor() {
    var _a;
    const { t } = useTranslation(['specification', 'common']);
    const dispatch = useDispatch();
    const modal = useSelector(selectModalByName('supplierEditor'));
    const supplierId = (_a = modal === null || modal === void 0 ? void 0 : modal.id) !== null && _a !== void 0 ? _a : '';
    const supplier = useSelector((state) => state.suppliers.items.find((item) => item.id === supplierId));
    const isLoading = useSelector((state) => state.suppliers.status.update === 'loading');
    const [supplierForm, setSupplierForm] = useState({
        fullName: '',
        shortName: '',
        code: '',
        vatNumber: '',
        countryOfOrigin: '',
        addressPostalCode: '',
        addressCity: '',
        addressStreet: '',
        addressNumber: '',
        emailAddress: '',
        contactPhone: '',
    });
    useEffect(() => {
        if (supplier) {
            setSupplierForm({
                fullName: supplier.fullName || '',
                shortName: supplier.shortName || '',
                code: supplier.code || '',
                vatNumber: supplier.vatNumber || '',
                countryOfOrigin: supplier.countryOfOrigin || '',
                addressPostalCode: supplier.addressPostalCode || '',
                addressCity: supplier.addressCity || '',
                addressStreet: supplier.addressStreet || '',
                addressNumber: supplier.addressNumber || '',
                emailAddress: supplier.emailAddress || '',
                contactPhone: supplier.contactPhone || '',
            });
        }
    }, [supplier]);
    const { errors, isValid, pendingFields, markTouched, validateAll } = useSupplierValidation(supplierForm, t, 'fullName');
    const handleCloseModal = () => {
        dispatch(closeModal({ name: 'supplierEditor' }));
    };
    const handleFormChange = ({ target: { name, value } }) => {
        setSupplierForm((spplr) => (Object.assign(Object.assign({}, spplr), { [name]: value })));
    };
    const handleFormSubmit = () => {
        if (!validateAll())
            return;
        dispatch(updateSupplier(Object.assign({ supplierId }, supplierForm)))
            .unwrap()
            .then(() => {
            dispatch(closeModal({ name: 'supplierEditor' }));
        });
    };
    useKeyDownListener([
        { code: 'Enter', callback: handleFormSubmit },
        { code: 'Escape', callback: handleCloseModal },
    ]);
    return (React.createElement(Modal, { name: "supplierEditor", size: "m", header: React.createElement(Label, { weight: "strong" },
            React.createElement(SuppliersSvg, null),
            t('edit_supplier')), footer: React.createElement(FormSaveActions, { isValid: isValid, isLoading: isLoading, pendingFields: pendingFields, onSave: handleFormSubmit, onCancel: handleCloseModal, validateAll: validateAll }) },
        React.createElement(Grid, { gap: 32 },
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:basic_info')),
                React.createElement(FormField, { direction: "row", label: t('supplier_name_capitalize'), error: errors.fullName },
                    React.createElement(Input, { value: supplierForm.fullName, onChange: handleFormChange, onBlur: () => markTouched('fullName'), name: "fullName", error: Boolean(errors.fullName), disabled: (supplier === null || supplier === void 0 ? void 0 : supplier.canFullNameBeUpdated) === false })),
                React.createElement(FormField, { direction: "row", label: t('supplier_shortname'), error: errors.shortName },
                    React.createElement(Input, { value: supplierForm.shortName, onChange: handleFormChange, onBlur: () => markTouched('shortName'), name: "shortName", error: Boolean(errors.shortName) })),
                React.createElement(FormField, { direction: "row", label: t('supplier_code'), error: errors.code },
                    React.createElement(Input, { value: supplierForm.code, onChange: handleFormChange, onBlur: () => markTouched('code'), name: "code", error: Boolean(errors.code) })),
                React.createElement(FormField, { direction: "row", label: t('specification:vat_number') },
                    React.createElement(Input, { value: supplierForm.vatNumber, onChange: handleFormChange, name: "vatNumber" }))),
            React.createElement(Grid, null,
                React.createElement(Label, null, t('common:address')),
                React.createElement(FormField, { direction: "row", label: t('specification:country_of_origin') },
                    React.createElement(Input, { value: supplierForm.countryOfOrigin, onChange: handleFormChange, name: "countryOfOrigin" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_postal_code') },
                    React.createElement(Input, { value: supplierForm.addressPostalCode, onChange: handleFormChange, name: "addressPostalCode" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_city') },
                    React.createElement(Input, { value: supplierForm.addressCity, onChange: handleFormChange, name: "addressCity" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_street') },
                    React.createElement(Input, { value: supplierForm.addressStreet, onChange: handleFormChange, name: "addressStreet" })),
                React.createElement(FormField, { direction: "row", label: t('specification:address_number') },
                    React.createElement(Input, { value: supplierForm.addressNumber, onChange: handleFormChange, name: "addressNumber" })),
                React.createElement(FormField, { direction: "row", label: t('specification:email_address') },
                    React.createElement(Input, { value: supplierForm.emailAddress, onChange: handleFormChange, name: "emailAddress" })),
                React.createElement(FormField, { direction: "row", label: t('specification:contact_phone') },
                    React.createElement(Input, { value: supplierForm.contactPhone, onChange: handleFormChange, name: "contactPhone" }))))));
}
export default memo(SupplierEditor);
